import { slices } from "@/constants/sliceNames";
import { createAsyncThunk } from "@reduxjs/toolkit";

import axiosIns from "../../../axios/defaultAxios";
import { endpoints } from "../../../axios/endpoints";
import { CameraStatus } from "../../../constants/camera-status";
import { Camera } from "../../../entities/Camera";
import { serializeQueryParams } from "../../../utils/utils";
import { errorMessage, successMessage } from "../../actions/messages.actions";

import { mapImagesToCameraImages, mapSingleImageToCameraImage } from "./utils";

const ENDPOINT = "/cameras";

/******* Get list of cameras ********/

export const requestListOfCameras = createAsyncThunk(
  `${slices.camera}/requestListOfCameras`,
  async (params = { sort: "-updated_at" }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosIns.get(`${ENDPOINT}?${serializeQueryParams(params)}`);
      if (response.status === 200) {
        return response.data.data;
      }
      throw new Error(response.data.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.error || error.message));
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const requestListOfCamerasStatus = {
  [requestListOfCameras.fulfilled]: (state, action) => {
    const { current_page, page_limit, records_count, total_pages, data } = action.payload;
    state.cameraList = data.map(Camera.fromApi);
    state.currentPage = current_page;
    state.pageLimit = page_limit;
    state.recordsCount = records_count;
    state.totalPages = total_pages;
    state.loading = false;
  },
  [requestListOfCameras.pending]: (state, action) => {
    state.loading = true;
  },
  [requestListOfCameras.rejected]: (state, action) => {
    state.loading = false;
    state.error = action.payload;
  },
};

/******* Get single camera details ********/

export const requestSingleCameraDetails = createAsyncThunk(
  `${slices.camera}/requestSingleCameraDetails`,
  async (cameraId, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosIns.get(`${ENDPOINT}/${cameraId}`);
      if (response.status === 200) {
        return response.data.data;
      }
      throw new Error(response.data.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.error || error.message));
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const requestSingleCameraDetailsStatus = {
  [requestSingleCameraDetails.fulfilled]: (state, action) => {
    state.camera = Camera.fromApi(action.payload);
    state.loading = false;
  },
  [requestSingleCameraDetails.pending]: (state, action) => {
    state.loading = true;
  },
  [requestSingleCameraDetails.rejected]: (state, action) => {
    state.loading = false;
    state.error = action.payload;
  },
};

/******* Get camera images ********/

export const requestCameraImages = createAsyncThunk(
  `${slices.camera}/requestCameraImages`,
  async ({ cameraId, imagesType, filters = { page: 1 } }, { rejectWithValue, dispatch }) => {
    try {
      const query = new URLSearchParams({
        ...filters,
        page: filters.page.toString(),
        images_type: imagesType,
      }).toString();
      const response = await axiosIns.get(`${ENDPOINT}/${cameraId}/images?${query}`);
      if (response.status === 200) {
        // process images
        const data = response.data.data;
        const imagesData = mapImagesToCameraImages(data, imagesType);
        return { res: { ...data, data: imagesData }, filters: filters };
      }
      throw new Error(response.data?.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.error || error.message));
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const requestCameraImagesStatus = {
  [requestCameraImages.fulfilled]: (state, action) => {
    const { res, filters } = action.payload;
    const { data, page_limit, records_count, total_pages } = res;
    state.images.imagesList = data;
    state.images.pageLimit = page_limit;
    state.images.recordsCount = records_count;
    state.images.totalPages = total_pages;
    state.images.loading = false;
    state.images.appliedFiltersOnImages = filters;
  },
  [requestCameraImages.pending]: (state, action) => {
    state.images.loading = true;
    state.images.imagesList = [];
    state.images.error = "";
    state.images.message = "";
  },
  [requestCameraImages.rejected]: (state, action) => {
    state.images.loading = false;
    state.images.error = action.payload;
  },
};

/******* Get single image details ********/

export const requestSingleImageDetails = createAsyncThunk(
  `${slices.camera}/requestSingleImageDetails`,
  async ({ cameraId, imageId, imageType, page = 1 }, { rejectWithValue, dispatch }) => {
    try {
      const query = new URLSearchParams({
        page: page.toString(),
        image_type: imageType,
      }).toString();
      const response = await axiosIns.get(`${ENDPOINT}/${cameraId}/image/${imageId}?${query}`);
      if (response.status === 200) {
        // process image
        const data = response.data.data;
        const imageData = mapSingleImageToCameraImage(data, imageType);
        return { data: imageData };
      }
      throw new Error(response.data.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.error || error.message));
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const requestSingleImageDetailsStatus = {
  [requestSingleImageDetails.fulfilled]: (state, action) => {
    const { data } = action.payload;
    state.images.image = data;
    state.images.loading = false;
  },
  [requestSingleImageDetails.pending]: (state, action) => {
    state.images.loading = true;
    state.images.error = "";
    state.images.message = "";
  },
  [requestSingleImageDetails.rejected]: (state, action) => {
    state.images.loading = false;
    state.images.error = action.payload;
  },
};

/******* Connect camera ********/

export const requestConnectCamera = createAsyncThunk(
  `${slices.camera}/requestConnectCamera`,
  async ({ cameraId, data = {} }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosIns.post(`${ENDPOINT}/connect/${cameraId}`, data);
      if (response.status === 200) {
        dispatch(successMessage(response.data.message));
        dispatch(requestSingleCameraDetails(cameraId));
        return response.data;
      }
      throw new Error(response.data.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.error || error.message));
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const requestConnectCameraStatus = {
  [requestConnectCamera.fulfilled]: (state, action) => {
    state.connection.loading = false;
    state.connection.error = "";
    state.connection.message = action.payload.message;
  },
  [requestConnectCamera.pending]: (state, action) => {
    state.connection.loading = true;
    state.connection.error = "";
    state.connection.message = "";
  },
  [requestConnectCamera.rejected]: (state, action) => {
    state.connection.loading = false;
    // used new String method to trigger useEffect in the requested component
    state.connection.error = new String(action.payload);
    state.connection.message = "";
  },
};

/******* Disconnect camera ********/

export const requestDisconnectCamera = createAsyncThunk(
  `${slices.camera}/requestDisconnectCamera`,
  async (cameraId, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosIns.post(`${ENDPOINT}/disconnect/${cameraId}`);
      if (response.status === 200) {
        dispatch(successMessage("Camera disconnected Successfully"));
        dispatch(requestSingleCameraDetails(cameraId));
        return response.data;
      }
      throw new Error(response.data.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.error || error.message));
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const requestDisconnectCameraStatus = {
  [requestDisconnectCamera.fulfilled]: (state, action) => {
    state.connection.loading = false;
    state.connection.error = "";
    state.connection.message = action.payload.message;
    state.camera.connected = false;
    state.loading = false;
  },
  [requestDisconnectCamera.pending]: (state, action) => {
    state.loading = true;
    state.connection.error = "";
    state.connection.message = "";
  },
  [requestDisconnectCamera.rejected]: (state, action) => {
    state.connection.loading = false;
    // used new String method to trigger useEffect in the requested component
    state.connection.error = new String(action.payload);
    state.connection.message = "";
  },
};

/******* Create camera ********/

export const requestCreateCamera = createAsyncThunk(
  `${slices.camera}/requestCreateCamera`,
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosIns.post(`${ENDPOINT}`, data);
      if (response.status === 200) {
        dispatch(successMessage(response.data.message));
        return response.data;
      }
      throw new Error(response.data.error);
    } catch (error) {
      if (error.response?.data?.error.includes("ResourceInUseException"))
        dispatch(errorMessage("Camera with this name is already created"));
      else dispatch(errorMessage(error.response?.data?.message || error.message));
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const requestCreateCameraStatus = {
  [requestCreateCamera.fulfilled]: (state, action) => {
    state.loading = false;
    state.error = "";
    state.camera = Camera.fromApi(action.payload.data);
    state.message = action.payload.message;
  },
  [requestCreateCamera.pending]: (state, action) => {
    state.loading = true;
    state.error = "";
    state.message = "";
  },
  [requestCreateCamera.rejected]: (state, action) => {
    state.loading = false;
    state.error = action.payload;
    state.message = "";
  },
};

/******* Update camera ********/

export const requestUpdateCamera = createAsyncThunk(
  `${slices.camera}/requestUpdateCamera`,
  async ({ cameraId, data }, { rejectWithValue, dispatch }) => {
    try {
      const body = {
        ...data,
        connection_status: CameraStatus.IN_CONFIGURATION,
      };
      const response = await axiosIns.put(`${ENDPOINT}/${cameraId}`, body);
      if (response.status === 200) {
        dispatch(successMessage(response.data.message));
        return response.data;
      }
      throw new Error(response.data.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.message || error.message));
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const requestUpdateCameraStatus = {
  [requestUpdateCamera.fulfilled]: (state, action) => {
    state.loading = false;
    state.error = "";
    state.message = action.payload.message;
  },
  [requestUpdateCamera.pending]: (state, action) => {
    state.loading = true;
    state.error = "";
    state.message = "";
  },
  [requestUpdateCamera.rejected]: (state, action) => {
    state.loading = false;
    state.error = action.payload;
    state.message = "";
  },
};

/******* Delete camera ********/

export const requestDeleteCamera = createAsyncThunk(
  `${slices.camera}/requestDeleteCamera`,
  async (cameraId, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosIns.delete(`${ENDPOINT}/${cameraId}`);
      if (response.status === 200) {
        dispatch(successMessage(response.data.message));
        dispatch(requestListOfCameras());
        return response.data;
      }
      throw new Error(response.data.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.message || error.message));
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const requestDeleteCameraStatus = {
  [requestDeleteCamera.fulfilled]: (state, action) => {
    state.loading = false;
    state.error = "";
    state.message = action.payload.message;
  },
  [requestDeleteCamera.pending]: (state, action) => {
    state.loading = true;
    state.error = "";
    state.message = "";
  },
  [requestDeleteCamera.rejected]: (state, action) => {
    state.loading = false;
    state.error = action.payload;
    state.message = "";
  },
};

/******* Test camera ********/

export const requestTestCamera = createAsyncThunk(
  `${slices.camera}/requestTestCamera`,
  async (cameraId, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosIns.post(`${endpoints.testCamera}/${cameraId}`);
      if (response.status === 200) {
        dispatch(successMessage("Camera connected, Starting Streaming..."));
        return response.data;
      }
      throw new Error(response.data.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.error || error.message));
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const requestTestCameraStatus = {
  [requestTestCamera.fulfilled]: (state, action) => {
    state.loading = false;
    state.error = "";
    state.message = action.payload.message;
    state.camera.connectionStatus = CameraStatus.CONNECTED;
  },
  [requestTestCamera.pending]: (state, action) => {
    state.loading = true;
    state.error = "";
    state.message = "";
  },
  [requestTestCamera.rejected]: (state, action) => {
    state.loading = false;
    state.error = action.payload;
    state.message = "";
    state.camera.connectionStatus = CameraStatus.TESTING;
  },
};

/******* Test camera config ********/

export const requestTestCameraConfig = createAsyncThunk(
  `${slices.camera}/requestTestCameraConfig`,
  async (cameraId, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosIns.post(`${endpoints.testCameraConfig}/${cameraId}`);
      if (response.status === 200) {
        dispatch(successMessage("Camera configured successfully"));
        dispatch(requestSingleCameraDetails(cameraId));
        return response.data;
      }
      throw new Error(response.data.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.error || error.message));
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const requestTestCameraConfigStatus = {
  [requestTestCameraConfig.fulfilled]: (state, action) => {
    state.loading = false;
    state.error = "";
    state.message = action.payload.message;
  },
  [requestTestCameraConfig.pending]: (state, action) => {
    state.loading = true;
    state.error = "";
    state.message = "";
  },
  [requestTestCameraConfig.rejected]: (state, action) => {
    state.loading = false;
    state.error = action.payload;
    state.message = "";
  },
};

/******* Get camera model details ********/

export const requestCameraModelDetails = createAsyncThunk(
  `${slices.camera}/requestCameraModelDetails`,
  async (modelId, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosIns.get(`models/v2/${modelId}`);
      if (response.status === 200) {
        return response.data.data;
      }
      throw new Error(response.data.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.error || error.message));
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const requestCameraModelDetailsStatus = {
  [requestCameraModelDetails.fulfilled]: (state, action) => {
    state.cameraModel = action.payload;
  },
};

/** start camera streaming */
export const startCameraStreaming = createAsyncThunk(
  `${slices.camera}/startCameraStreaming`,
  async ({ cameraId, cameraName, localSDPToken }, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await axiosIns.post(`cameras/stream/${cameraId}`, {
        token: localSDPToken,
      });
      // await dispatch(startWebRTCSession({ remoteSDP: response.data.data, cameraId }));
      dispatch(successMessage(`Streaming camera ${cameraName}`));
      return response.data.data;
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.error || error.message));
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const startCameraStreamingStatuses = {
  [startCameraStreaming.fulfilled]: (state, action) => {
    state.streaming.isStreaming = true;
    state.streaming.prediction = null;
    state.streaming.loading = false;
  },
  [startCameraStreaming.pending]: (state, action) => {
    state.streaming.loading = true;
  },
  [startCameraStreaming.rejected]: (state, action) => {
    state.loading = false;
    state.error = action.payload;
    state.message = "";
  },
};
/** Get number of images in camera */

export const requestNumberOfImages = createAsyncThunk(
  `${slices.camera}/requestNumberOfImages`,
  async ({ cameraId, modelType }, { rejectWithValue, dispatch }) => {
    try {
      const params = {
        image_type: modelType,
      };
      const response = await axiosIns.get(`${ENDPOINT}/${cameraId}/image/count?${serializeQueryParams(params)}`);
      if (response.status === 200) {
        return { cameraId, size: response.data.data };
      }
      throw new Error(response.data.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.error || error.message));
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const requestNumberOfImagesStatus = {
  [requestNumberOfImages.fulfilled]: (state, action) => {
    const { cameraId, size } = action.payload;
    const index = state.cameraList.findIndex((camera) => camera.id === cameraId);
    if (index !== -1) state.cameraList[index].size = size;
    state.cameraSize = size;
  },
};

/** Remove camera from a group */

export const requestRemoveCameraFromGroup = createAsyncThunk(
  `${slices.camera}/requestRemoveCameraFromGroup`,
  async ({ cameraId, showNotification = true }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosIns.post(`${ENDPOINT}/${cameraId}/group/remove`);
      if (response.status === 200) {
        if (showNotification) dispatch(successMessage("Camera removed from group successfully"));
        dispatch(requestSingleCameraDetails(cameraId));
        return response.data;
      }
      throw new Error(response.data.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.error || error.message));
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const requestRemoveCameraFromGroupStatus = {
  [requestRemoveCameraFromGroup.fulfilled]: (state, action) => {
    state.loading = false;
    state.error = "";
    state.message = action.payload.message;
    state.camera.groupId = "";
  },
  [requestRemoveCameraFromGroup.pending]: (state, action) => {
    state.loading = true;
    state.error = "";
    state.message = "";
  },
  [requestRemoveCameraFromGroup.rejected]: (state, action) => {
    state.loading = false;
    state.error = action.payload;
    state.message = "";
  },
};
